// Shared UI components extracted from App.js for reuse across pages
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { X, Star, Zap, Compass, Gift, Book, Trophy, Check, Sparkles, Eye } from "lucide-react";
import { useApp, IMG, API } from "../context";
import axios from "axios";
import VictoryCelebration from "./VictoryCelebration";
import { formatApiDetail } from "../utils/showApiError";

export const SparkleField = ({ count = 20 }) => (
  <div className="sparkle-field">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="sparkle-particle" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 4}s`, animationDuration: `${2 + Math.random() * 3}s` }} />
    ))}
  </div>
);

export const DayNightCycle = () => (
  <div className="day-night-cycle" data-testid="day-night-cycle">
    <div className="day-night-overlay" />
    <div className="night-stars">
      {[...Array(50)].map((_, i) => (
        <div key={i} className="night-star" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 55}%`, animationDelay: `${Math.random() * 5}s`, animationDuration: `${2 + Math.random() * 4}s`, width: `${1 + Math.random() * 2.5}px`, height: `${1 + Math.random() * 2.5}px` }} />
      ))}
    </div>
  </div>
);

export const ShootingStars = () => (
  <div className="shooting-star-layer">
    <div className="shooting-star ss-1" />
    <div className="shooting-star ss-2" />
    <div className="shooting-star ss-3" />
  </div>
);

export const ForegroundSparkles = () => (
  <div className="foreground-sparkle-layer">
    {[...Array(8)].map((_, i) => (
      <div key={i} className="fg-sparkle" style={{ left: `${10 + Math.random() * 80}%`, bottom: `${Math.random() * 30}%`, animationDelay: `${Math.random() * 6}s`, animationDuration: `${4 + Math.random() * 4}s` }} />
    ))}
  </div>
);

export const ParallaxStars = () => (
  <div className="parallax-far-stars">
    {[...Array(40)].map((_, i) => (
      <div key={i} className="parallax-far-star" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 50}%`, width: `${0.5 + Math.random() * 1.5}px`, height: `${0.5 + Math.random() * 1.5}px`, animationDelay: `${Math.random() * 8}s`, animationDuration: `${3 + Math.random() * 5}s`, opacity: 0.1 + Math.random() * 0.3 }} />
    ))}
  </div>
);

export const ExplorerPath = () => (
  <svg className="explorer-path" viewBox="0 0 1000 200" preserveAspectRatio="none" data-testid="explorer-path">
    <defs>
      <linearGradient id="pathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
        <stop offset="25%" stopColor="#8B5CF6" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#F97316" stopOpacity="0.8" />
        <stop offset="75%" stopColor="#EAB308" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#22C55E" stopOpacity="0.8" />
      </linearGradient>
      <filter id="pathGlow"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
    </defs>
    <path d="M50,100 C200,30 300,170 500,100 C700,30 800,170 950,100" stroke="url(#pathGrad)" strokeWidth="3" fill="none" filter="url(#pathGlow)" opacity="0.7" strokeDasharray="8 6" className="explorer-path-line" />
    {[0.12, 0.37, 0.62, 0.87].map((offset, i) => (
      <circle key={i} r="5" fill={["#3B82F6","#8B5CF6","#F97316","#EAB308"][i]} className="path-node" style={{ offsetPath: "path('M50,100 C200,30 300,170 500,100 C700,30 800,170 950,100')", offsetDistance: `${offset * 100}%` }}>
        <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
      </circle>
    ))}
    <circle r="4" fill="white" className="path-traveler">
      <animateMotion dur="12s" repeatCount="indefinite" path="M50,100 C200,30 300,170 500,100 C700,30 800,170 950,100" />
      <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
    </circle>
  </svg>
);

export const ExplorerIdentityPanel = ({ progress, user }) => {
  if (!user) {
    return (
      <div className="explorer-identity" data-testid="explorer-identity-guest">
        <div className="explorer-identity-avatar"><Star size={20} className="text-white" /></div>
        <div className="explorer-identity-info">
          <p className="explorer-identity-name">New Explorer</p>
          <p className="explorer-identity-level">Create your Isaiah&apos;s World ID to begin!</p>
        </div>
      </div>
    );
  }
  const level = progress?.explorer_level || 1;
  const stars = progress?.stars || 0;
  const adventures = progress?.adventures_explored || 0;
  return (
    <div className="explorer-identity" data-testid="explorer-identity">
      <div className="explorer-identity-avatar">
        <span className="text-white font-fredoka font-bold text-lg">{(user.first_name || "E")[0]}</span>
      </div>
      <div className="explorer-identity-info">
        <p className="explorer-identity-name">{user.first_name || "Explorer"}</p>
        <p className="explorer-identity-level">
          <Sparkles size={10} className="text-violet-400" /> Level {level} Explorer · {adventures} Adventures
        </p>
      </div>
      <div className="explorer-identity-stars">
        <Star size={14} className="text-yellow-400" />
        <span>{stars}</span>
      </div>
    </div>
  );
};

export const AuthGate = ({ onClose }) => {
  const navigate = useNavigate();
  const { login, loginAsDemo, user } = useApp();
  const [mode, setMode] = useState("options"); // "options" | "login"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // GLOBAL SAFETY NET (2026-05-15 round 34), per user request:
  // AuthGate should NEVER appear for a user who is already logged in.
  // If `useApp().user` exists OR a real auth token sits in storage,
  // we silently dismiss this gate. Many call-sites flip `showGate` true
  // via stale state or async load races; this single guard makes the
  // entire app immune to those bugs without touching 17 individual pages.
  // We intentionally do NOT treat the demo session as logged in, demo
  // users should still see the upgrade gate when hitting a protected tool.
  const isRealLoggedIn = useMemo(() => {
    if (user && !user.isDemo) return true;
    try {
      const tok = localStorage.getItem("isaiah_token") || sessionStorage.getItem("isaiah_token");
      return Boolean(tok && tok !== "demo-token");
    } catch (_e) {
      return false;
    }
  }, [user]);

  useEffect(() => {
    if (isRealLoggedIn) {
      // Fire onClose on the next tick so callers' setShowGate(false)
      // doesn't fight with our render-time short-circuit.
      const t = setTimeout(() => { try { onClose && onClose(); } catch (_e) {} }, 0);
      return () => clearTimeout(t);
    }
  }, [isRealLoggedIn, onClose]);

  if (isRealLoggedIn) return null;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API}/api/auth/login`, { email, password });
      login(res.data.token, res.data.user);
      onClose();
    } catch (err) {
      setError(formatApiDetail(err.response?.data?.detail, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-gate-overlay" data-testid="auth-gate-modal" onClick={onClose}>
      <div className="auth-gate-card" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="auth-gate-close" data-testid="auth-gate-close"><X size={20} /></button>
        <img src={IMG.isaiahAvatar} alt="Isaiah" loading="lazy" className="auth-gate-avatar" />

        {mode === "login" ? (
          <>
            <h2 className="text-2xl font-fredoka font-bold text-white mt-3">Welcome Back!</h2>
            <p className="text-slate-400 mt-2 text-center text-sm">Sign in to save your artwork</p>
            <form onSubmit={handleLogin} className="flex flex-col gap-3 mt-4 w-full">
              {error && <p className="text-red-400 text-xs bg-red-500/10 px-3 py-1.5 rounded-lg text-center">{error}</p>}
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address"
                className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none" required autoFocus data-testid="auth-gate-email" />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
                className="w-full bg-white/[0.06] border border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none" required data-testid="auth-gate-password" />
              <button type="submit" disabled={loading} className="btn-universe w-full" data-testid="auth-gate-submit">
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <button type="button" onClick={() => setMode("options")} className="text-slate-400 hover:text-white text-sm transition-colors">Back</button>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-fredoka font-bold text-white mt-3">Ready to Start Your Journey?</h2>
            <p className="text-slate-400 mt-2 text-center text-sm">Create your free account to save your stars and unlock the full experience!</p>
            <div className="flex flex-col gap-3 mt-5 w-full">
              <button onClick={() => { onClose(); navigate("/signup"); }} className="btn-universe w-full" data-testid="auth-gate-signup">Create My Free Account</button>
              <button onClick={() => setMode("login")} className="btn-universe-outline w-full" data-testid="auth-gate-login">I Already Have One</button>
            </div>
          </>
        )}
        <p className="text-slate-500 text-xs mt-4 text-center">Free Basic Account Forever · No credit card needed.</p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, authStatus } = useApp();
  // Demo/guest users are NOT real accounts. Treat them as unauthenticated for
  // strict-protected routes (e.g. Parent Corner, Profile, Settings) so they
  // are prompted to sign up instead of seeing surfaces that need real data.
  const isRealUser = !!user && user.isDemo !== true;
  const prelaunch = (process.env.REACT_APP_PRELAUNCH_MODE ?? "true").toLowerCase() !== "false";

  // Stable token-presence check that doesn't lag the React context. If a
  // token is in storage the user IS logged in, the context just hasn't
  // hydrated yet. Without this guard, navigating between protected pages
  // (e.g. /learn → /learn/upload/library) can race and pop the AuthGate
  // for a fully-logged-in user before the useApp() user object lands.
  const hasToken = (() => {
    try {
      return !!(
        localStorage.getItem("isaiah_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("isaiah_token") ||
        sessionStorage.getItem("token")
      );
    } catch { return false; }
  })();

  const [showGate, setShowGate] = useState(!isRealUser && !hasToken && !prelaunch && authStatus !== "loading");
  // Route-level fallback: show a tiny spinner ONLY in the (rare) case
  // authStatus is still loading and we somehow rendered anyway (e.g.
  // demo-token path is in mid-transition), OR we have a token but the
  // user object hasn't hydrated yet.
  if ((authStatus === "loading" && !user) || (hasToken && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="protected-route-loading">
        <div className="animate-pulse text-white/30 text-sm">Loading…</div>
      </div>
    );
  }
  // Guest/demo roam session: a real guest token IS in storage, so the visitor
  // is authenticated (just as a limited guest). Let them through — the
  // GuestSystem 5-minute timer handles expiry & sign-up prompting separately.
  if (user?.isDemo === true && hasToken) return children;
  // In pre-launch mode every visitor with this URL is a personal connection
  // (friends/family). No guest-browse modal, funnel them straight to signup.
  if (!isRealUser && !hasToken && prelaunch) return <Navigate to="/signup" replace />;
  if (!isRealUser && !hasToken && showGate) return <AuthGate onClose={() => setShowGate(false)} />;
  if (!isRealUser && !hasToken) return <Navigate to="/signup" replace />;
  return children;
};

// PreviewRoute: allows guests to VIEW the page with a preview indicator, blocks when expired
export const PreviewRoute = ({ children, feature = "this feature" }) => {
  const { user, authStatus } = useApp();
  const prelaunch = (process.env.REACT_APP_PRELAUNCH_MODE ?? "true").toLowerCase() !== "false";
  // Same lag-proof token-presence check used by ProtectedRoute so a real
  // logged-in user never gets bounced to /signup mid-navigation.
  const hasToken = (() => {
    try {
      return !!(
        localStorage.getItem("isaiah_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("isaiah_token") ||
        sessionStorage.getItem("token")
      );
    } catch { return false; }
  })();
  const [showGate, setShowGate] = useState(!user && !hasToken && !prelaunch && authStatus !== "loading");
  if ((authStatus === "loading" && !user) || (hasToken && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="preview-route-loading">
        <div className="animate-pulse text-white/30 text-sm">Loading…</div>
      </div>
    );
  }
  // Guest/demo roam session with a real guest token in storage → allow the
  // preview through (5-minute GuestSystem timer governs expiry).
  if (user?.isDemo === true && hasToken) {
    return <div className="relative">{children}</div>;
  }
  // Pre-launch: no guest preview, straight to signup.
  if (!user && !hasToken && prelaunch) return <Navigate to="/signup" replace />;
  if (!user && !hasToken && showGate) return <AuthGate onClose={() => setShowGate(false)} />;
  if (!user && !hasToken) return <Navigate to="/signup" replace />;
  // Guest users see the page with a preview indicator (post-launch only)
  if (user?.isDemo) {
    return (
      <div className="relative">
        {children}
      </div>
    );
  }
  return children;
};

export const VictoryMoment = ({ victory, onClose }) => {
  const { user } = useApp();
  const [victoryAnim, setVictoryAnim] = useState(null);
  useEffect(() => {
    const uid = user?.id || user?.email;
    if (!uid || !victory) return;
    axios.get(`${API}/arcade-items/my-arcade-inventory/${encodeURIComponent(uid)}`)
      .then(r => setVictoryAnim(r.data?.victory_animation || null))
      .catch(() => {});
  }, [user, victory]);
  useEffect(() => { const t = setTimeout(onClose, 5500); return () => clearTimeout(t); }, [onClose]);
  if (!victory) return null;
  const colorMap = {
    mission: "#F97316", adventure: "#3B82F6",
    discovery: "#A855F7", learning: "#22C55E",
    level_up: "#FBBF24", milestone: "#8B5CF6",
    streak: "#EF4444", reward: "#10B981"
  };
  const iconMap = {
    mission: Zap, adventure: Compass, discovery: Gift, learning: Book,
    level_up: Trophy, milestone: Star, streak: Zap, reward: Gift
  };
  const VIcon = iconMap[victory.type] || Star;
  const color = colorMap[victory.type] || "#3B82F6";
  return (
    <>
      {victoryAnim && <VictoryCelebration victoryAnim={victoryAnim} duration={3500} />}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9998] animate-in slide-in-from-top fade-in duration-500 pointer-events-auto" data-testid="victory-moment">
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl border shadow-2xl" style={{ background: "rgba(15,23,42,0.92)", borderColor: `${color}30`, boxShadow: `0 8px 32px ${color}15` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
            <VIcon size={20} style={{ color }} />
          </div>
          <div>
            <h4 className="text-white font-fredoka font-bold text-sm">{victory.title}</h4>
            <p className="text-white/50 text-xs">{victory.message}</p>
            {victory.stars > 0 && <div className="flex items-center gap-1 mt-0.5"><Star className="text-yellow-300" size={10} /><span className="text-yellow-200 text-[10px] font-bold">+{victory.stars} Stars!</span></div>}
          </div>
        </div>
        <div className="absolute bottom-0 left-5 right-5 h-0.5 rounded-full overflow-hidden" style={{ background: `${color}10` }}>
          <div className="h-full rounded-full animate-[shrink_5.5s_linear_forwards]" style={{ background: color }} />
        </div>
      </div>
    </>
  );
};

export const SurpriseDiscovery = ({ discovery, onCollect }) => {
  useEffect(() => {
    if (!discovery) return;
    const t = setTimeout(() => { onCollect(); }, 5000);
    return () => clearTimeout(t);
  }, [discovery, onCollect]);
  if (!discovery) return null;
  const rarityColors = { common: "#94A3B8", uncommon: "#34D399", rare: "#A78BFA", legendary: "#FBBF24" };
  const color = rarityColors[discovery.rarity] || rarityColors.common;
  return (
    <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-right fade-in duration-500 pointer-events-auto" data-testid="surprise-discovery">
      <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl border shadow-2xl max-w-sm" style={{ background: "rgba(15,23,42,0.92)", borderColor: `${color}30`, boxShadow: `0 8px 32px ${color}15` }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
          <Gift size={20} style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-white font-fredoka font-bold text-sm truncate">{discovery.title}</h4>
          <p className="text-white/50 text-xs truncate">{discovery.message}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <Star className="text-yellow-300" size={10} />
            <span className="text-yellow-200 text-[10px] font-bold">+{discovery.stars} Stars</span>
            <span className="text-[9px] uppercase font-bold ml-1 px-1.5 py-0.5 rounded-full" style={{ color, background: `${color}15` }}>{discovery.rarity}</span>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-5 right-5 h-0.5 rounded-full overflow-hidden" style={{ background: `${color}10` }}>
        <div className="h-full rounded-full animate-[shrink_5s_linear_forwards]" style={{ background: color }} />
      </div>
    </div>
  );
};

export const GUIDE_MESSAGES = [
  { trigger: "welcome", text: "Welcome to my world! Pick an adventure below!" },
  { trigger: "mission_available", text: "You have a new mission! Let's earn some stars!" },
  { trigger: "first_learn", text: "Ready to learn something new? I'll help you!" },
  { trigger: "achievement", text: "Awesome job! You're an amazing explorer!" },
  { trigger: "return", text: "Welcome back, explorer! What shall we do today?" },
  { trigger: "idle", text: "Try clicking on one of the world portals!" },
  { trigger: "event", text: "A special event is happening! Check it out!" },
  { trigger: "reward", text: "You found a hidden treasure! Collect it!" },
];

export const IsaiahGuide = ({ message, show, onDismiss }) => {
  useEffect(() => { if (show) { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); } }, [show, onDismiss]);
  if (!show) return null;
  return (
    <div className="isaiah-guide" data-testid="guide-bubble">
      <img src={IMG.isaiahAvatar} alt="Isaiah" loading="lazy" className="guide-character" data-testid="isaiah-character" />
      <div className="guide-speech">
        <p>{message}</p>
        <button onClick={onDismiss} className="guide-dismiss">Got it!</button>
      </div>
    </div>
  );
};

export const ExplorerBadge = ({ progress }) => {
  if (!progress) return null;
  return (
    <div className="explorer-badge" data-testid="explorer-badge">
      <div className="explorer-badge-icon"><Star size={14} className="text-yellow-300" /></div>
      <span className="explorer-badge-text">{progress.stars}</span>
    </div>
  );
};

export const PortalTransition = ({ active, color, onDone }) => {
  useEffect(() => {
    if (active) { const t = setTimeout(onDone, 600); return () => clearTimeout(t); }
  }, [active, onDone]);
  if (!active) return null;
  return (
    <div className={`portal-enter-fx active`} style={{ background: `radial-gradient(circle at center, ${color}88, ${color}44, transparent)` }} />
  );
};
